<script setup>
/**
 * Device check — three sections on one page:
 *
 *   1. Environment banner showing detected OS + browser (so the user trusts
 *      the troubleshooting is matched to their setup).
 *   2. Speaker test — looping WebAudio arpeggio. No asset. No network.
 *      The user adjusts volume while it plays, then confirms yes/no.
 *   3. Microphone test — getUserMedia + level meter. The user speaks and
 *      watches the bar; on browser block, an inline remediation guides them
 *      to the fix.
 *   4. Troubleshooting — auto-detects the browser/OS, shows step-by-step
 *      instructions to allow microphone access. A manual picker lets the
 *      user override if our detection is wrong (e.g. they're on a desktop
 *      app that lies about its UA).
 *
 * Bottom of the page: "Copy diagnostics JSON" button (the support-handoff
 * payload pattern from iLearn).
 *
 * The detection / error-mapping / audio plumbing all lives in
 * composables/useSystemCheck.js (framework-agnostic). This file is just the
 * Vue UI on top.
 */
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import {
  detectEnvironment,
  describeMediaError,
  enumerateAudioDevices,
  playArpeggio,
  openMicStream,
  createMicLevelMeter,
  troubleshootingFor,
  troubleshootingKeys,
  collectDiagnostics
} from "../../composables/useSystemCheck.js";

// ── Environment + device counts ────────────────────────────────────────
const env = ref({ browser: "", os: "", isSecureContext: true, isInAppBrowser: false, inAppBrowserName: null });
const devices = ref({ microphones: 0, speakers: 0, supported: false });

// ── Speaker test ───────────────────────────────────────────────────────
const speaker = ref({ playing: false, status: "idle", message: "" });
let speakerHandle = null;

function startSpeaker() {
  stopSpeaker();
  speakerHandle = playArpeggio();
  speaker.value = {
    playing: true,
    status: "running",
    message: "A short tune is looping. Turn your volume up until you can hear it clearly."
  };
}

function stopSpeaker() {
  if (speakerHandle) {
    speakerHandle.stop();
    speakerHandle = null;
  }
  if (speaker.value.playing) speaker.value.playing = false;
}

function confirmSpeaker(heard) {
  stopSpeaker();
  speaker.value.status = heard ? "pass" : "warn";
  speaker.value.message = heard
    ? "Speakers are working."
    : "No sound heard. Check your volume is up, the right output device is selected, and your headphones are plugged in if you use them.";
}

// ── Microphone test ────────────────────────────────────────────────────
const mic = ref({
  status: "idle",         // idle | requesting | measuring | pass | warn | fail
  message: "",
  remediation: "",
  errorName: null,
  level: 0,               // 0..100, animated by the level meter
  peak: 0,                // session-high level
  recordedUrl: null,      // object URL for <audio controls> playback
  recordedBytes: 0,       // size of the recorded blob, for the "X KB" hint
  recordedSeconds: 0      // duration of the recorded clip
});
let micStream = null;
let micMeterHandle = null;
let micRecorder = null;
let micChunks = [];
let micRecordStartedAt = 0;

// Pick the best mimeType the browser supports for short-form voice.
// Most browsers handle their own default best — leave the option off and let
// MediaRecorder pick. Chrome → webm/opus; Firefox → ogg/opus; Safari → mp4/aac.
function preferredMicMime() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

async function startMic() {
  stopMic();

  if (!env.value.isSecureContext) {
    mic.value.status = "fail";
    mic.value.message = "Not a secure (https) connection";
    mic.value.remediation =
      "Microphone access only works on https sites. Open this page using the secure web address (https://app.i-recall.au).";
    return;
  }

  mic.value = {
    status: "requesting",
    message: "Asking your browser for microphone access…",
    remediation: "",
    errorName: null,
    level: 0, peak: 0,
    recordedUrl: null, recordedBytes: 0, recordedSeconds: 0
  };
  try {
    micStream = await openMicStream();
    mic.value.status = "measuring";
    mic.value.message = "Speak now — say a sentence or two so you can hear it back. Watch the bar move.";

    // Live level meter (same as before).
    micMeterHandle = createMicLevelMeter(micStream, (level) => {
      mic.value.level = level;
      if (level > mic.value.peak) mic.value.peak = level;
    });

    // Recorder, in parallel — captures the audio so we can play it back when
    // the user clicks "I'm done speaking". The level meter and the recorder
    // share the same MediaStream; they don't fight.
    micChunks = [];
    const mime = preferredMicMime();
    try {
      micRecorder = mime
        ? new MediaRecorder(micStream, { mimeType: mime })
        : new MediaRecorder(micStream);
    } catch {
      // Fallback: browser refused our mimeType. Let it pick the default.
      micRecorder = new MediaRecorder(micStream);
    }
    micRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) micChunks.push(e.data);
    };
    micRecordStartedAt = performance.now();
    micRecorder.start();
  } catch (err) {
    const d = describeMediaError(err);
    mic.value.status = "fail";
    mic.value.message = d.title;
    mic.value.remediation = d.remediation;
    mic.value.errorName = err.name || null;
  }
}

function finishMic() {
  // If a recording is in flight, wait for its onstop so the blob is complete
  // before we surface the playback URL. Without this we sometimes get an
  // empty/very short clip because chunks arrive after stop().
  const recorder = micRecorder;
  if (recorder && recorder.state !== "inactive") {
    recorder.onstop = () => finalizeMicRecording();
    try { recorder.stop(); } catch { finalizeMicRecording(); }
  } else {
    finalizeMicRecording();
  }
}

function finalizeMicRecording() {
  const durationSeconds = micRecordStartedAt
    ? Math.max(0, (performance.now() - micRecordStartedAt) / 1000)
    : 0;
  let blob = null;
  if (micChunks.length > 0) {
    const mime = (micRecorder && micRecorder.mimeType) || "audio/webm";
    blob = new Blob(micChunks, { type: mime });
  }

  // Verdict + message based on peak. The recording is a bonus — even a
  // quiet/short recording is still a "warn" with playback so the user can
  // hear why.
  if (mic.value.peak >= 10) {
    mic.value.status = "pass";
    mic.value.message = `Microphone working (peak level ${mic.value.peak}). Play it back to check quality.`;
  } else {
    mic.value.status = "warn";
    mic.value.message =
      "We didn't pick up much sound. Play it back to confirm — then check the right microphone is selected and increase your input volume in your OS sound settings.";
    mic.value.remediation =
      "Increase your input volume in your operating system sound settings and make sure the right microphone is selected, then try again.";
  }

  if (blob && blob.size > 0) {
    if (mic.value.recordedUrl) URL.revokeObjectURL(mic.value.recordedUrl);
    mic.value.recordedUrl = URL.createObjectURL(blob);
    mic.value.recordedBytes = blob.size;
    mic.value.recordedSeconds = Math.round(durationSeconds * 10) / 10;
  }

  stopMic();
}

function stopMic() {
  if (micMeterHandle) {
    micMeterHandle.stop();
    micMeterHandle = null;
  }
  if (micRecorder) {
    try {
      if (micRecorder.state !== "inactive") micRecorder.stop();
    } catch {}
    micRecorder = null;
  }
  if (micStream) {
    try { micStream.getTracks().forEach((t) => t.stop()); } catch {}
    micStream = null;
  }
  micRecordStartedAt = 0;
}

function clearRecording() {
  if (mic.value.recordedUrl) {
    URL.revokeObjectURL(mic.value.recordedUrl);
  }
  mic.value.recordedUrl = null;
  mic.value.recordedBytes = 0;
  mic.value.recordedSeconds = 0;
}

// ── Troubleshooting ────────────────────────────────────────────────────
const remedyKeys = troubleshootingKeys();
const remedyOverride = ref("");

const remedy = computed(() => troubleshootingFor(env.value, remedyOverride.value || null));

// ── Copy diagnostics ───────────────────────────────────────────────────
const copyMsg = ref("");

async function copyDiagnostics() {
  const blob = collectDiagnostics({
    environment: env.value,
    devices: devices.value,
    speaker: { status: speaker.value.status, message: speaker.value.message },
    microphone: {
      status: mic.value.status,
      message: mic.value.message,
      remediation: mic.value.remediation,
      errorName: mic.value.errorName,
      peak: mic.value.peak
    }
  });
  const json = JSON.stringify(blob, null, 2);
  try {
    await navigator.clipboard.writeText(json);
    copyMsg.value = "Diagnostics copied to clipboard. Paste them into an email to support.";
  } catch {
    copyMsg.value = "Could not copy to clipboard automatically. Open the browser console to grab the JSON manually.";
    // Last-resort: log it so the user can copy from devtools.
    // eslint-disable-next-line no-console
    console.log("=== i-Recall diagnostics ===\n" + json);
  }
  setTimeout(() => { copyMsg.value = ""; }, 5000);
}

// ── Reset ──────────────────────────────────────────────────────────────
function resetAll() {
  stopSpeaker();
  stopMic();
  clearRecording();
  speaker.value = { playing: false, status: "idle", message: "" };
  mic.value = {
    status: "idle", message: "", remediation: "", errorName: null,
    level: 0, peak: 0,
    recordedUrl: null, recordedBytes: 0, recordedSeconds: 0
  };
}

// ── Lifecycle ──────────────────────────────────────────────────────────
onMounted(async () => {
  env.value = detectEnvironment();
  devices.value = await enumerateAudioDevices();
});

onBeforeUnmount(() => {
  stopSpeaker();
  stopMic();
  clearRecording();
});

// ── Helpers ────────────────────────────────────────────────────────────
function statusClass(status) {
  return {
    pass: "badge bg-success",
    warn: "badge bg-warning text-dark",
    fail: "badge bg-danger",
    running: "badge bg-info text-dark",
    requesting: "badge bg-info text-dark",
    measuring: "badge bg-info text-dark"
  }[status] || "badge bg-secondary";
}

function statusLabel(status) {
  return {
    pass: "Passed",
    warn: "Check failed",
    fail: "Failed",
    running: "In progress",
    requesting: "Asking…",
    measuring: "Measuring…",
    idle: "Not started"
  }[status] || status;
}
</script>

<template>
  <!-- Environment banner -->
  <div class="alert alert-light border d-flex align-items-center gap-3 mb-3" role="status">
    <i class="bi bi-info-circle fs-4 text-primary"></i>
    <div class="flex-grow-1">
      <strong>We've detected:</strong>
      {{ env.browser || "(unknown browser)" }} on {{ env.os || "(unknown OS)" }}
      <span v-if="!env.isSecureContext" class="badge bg-danger ms-2">Insecure (not https)</span>
      <span v-if="env.isInAppBrowser" class="badge bg-warning text-dark ms-2">
        In-app browser ({{ env.inAppBrowserName }})
      </span>
      <div v-if="devices.supported" class="small text-muted mt-1">
        Detected {{ devices.microphones }} microphone{{ devices.microphones === 1 ? "" : "s" }} and
        {{ devices.speakers }} speaker{{ devices.speakers === 1 ? "" : "s" }}.
        <span v-if="devices.microphones > 0" class="text-muted">(Names appear after you allow access.)</span>
      </div>
    </div>
  </div>

  <div v-if="env.isInAppBrowser" class="alert alert-warning d-flex align-items-start gap-2 mb-3">
    <i class="bi bi-exclamation-triangle-fill fs-5 mt-1"></i>
    <div>
      <strong>You're inside {{ env.inAppBrowserName }}'s built-in browser.</strong>
      These embedded browsers usually block microphone access entirely. Open this page in
      Safari or Chrome directly — see <em>Troubleshooting</em> below.
    </div>
  </div>

  <!-- Speaker test -->
  <div class="card border-0 shadow-sm mb-4">
    <div class="card-body">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h2 class="h5 mb-0"><i class="bi bi-volume-up-fill me-2"></i>Speaker test</h2>
        <span :class="statusClass(speaker.status)">{{ statusLabel(speaker.status) }}</span>
      </div>
      <p class="text-muted small mb-3">
        Plays a short looping tune. Turn the volume up to a comfortable level —
        the tune keeps repeating until you stop it. No microphone needed.
      </p>

      <div class="d-flex flex-wrap gap-2 mb-2">
        <button v-if="!speaker.playing" type="button" class="btn btn-primary" @click="startSpeaker">
          <i class="bi bi-play-fill me-1"></i> Play test sound
        </button>
        <template v-else>
          <button type="button" class="btn btn-success" @click="confirmSpeaker(true)">
            <i class="bi bi-check-lg me-1"></i> I can hear it clearly
          </button>
          <button type="button" class="btn btn-outline-warning" @click="confirmSpeaker(false)">
            <i class="bi bi-x-lg me-1"></i> I can't hear it
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="stopSpeaker">Stop</button>
        </template>
      </div>

      <p v-if="speaker.message" class="mb-0 small" :class="{ 'text-success': speaker.status === 'pass', 'text-warning': speaker.status === 'warn' }">
        {{ speaker.message }}
      </p>
    </div>
  </div>

  <!-- Microphone test -->
  <div class="card border-0 shadow-sm mb-4">
    <div class="card-body">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h2 class="h5 mb-0"><i class="bi bi-mic-fill me-2"></i>Microphone test</h2>
        <span :class="statusClass(mic.status)">{{ statusLabel(mic.status) }}</span>
      </div>
      <p class="text-muted small mb-3">
        Asks your browser for microphone access, records what you say, and
        plays it back so you can judge the sound quality, microphone
        placement, and background noise.
      </p>

      <div class="d-flex flex-wrap gap-2 mb-3">
        <button
          v-if="mic.status === 'idle' || mic.status === 'fail' || mic.status === 'pass' || mic.status === 'warn'"
          type="button"
          class="btn btn-primary"
          @click="startMic"
        >
          <i class="bi bi-record-circle me-1"></i>
          {{ mic.status === "idle" ? "Start microphone test" : "Run again" }}
        </button>
        <template v-if="mic.status === 'measuring'">
          <button type="button" class="btn btn-success" @click="finishMic">
            <i class="bi bi-check-lg me-1"></i> I'm done speaking
          </button>
        </template>
      </div>

      <!-- Level meter (visible during measuring and persists peak) -->
      <div v-if="mic.status === 'measuring' || mic.peak > 0" class="mic-meter mb-2">
        <div class="mic-meter-fill" :style="{ width: mic.level + '%' }"></div>
        <div class="mic-meter-peak" :style="{ left: 'calc(' + mic.peak + '% - 2px)' }" :title="'Peak: ' + mic.peak"></div>
      </div>

      <p v-if="mic.message" class="mb-1 small" :class="{ 'text-success': mic.status === 'pass', 'text-warning': mic.status === 'warn', 'text-danger': mic.status === 'fail' }">
        {{ mic.message }}
      </p>
      <p v-if="mic.remediation" class="small text-muted mb-2">{{ mic.remediation }}</p>

      <!-- Playback of the user's recording -->
      <div v-if="mic.recordedUrl" class="border rounded p-3 mt-3" style="background: #fafafa">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong class="small">
            <i class="bi bi-soundwave me-1"></i>
            Your recording
          </strong>
          <span class="text-muted small">
            {{ mic.recordedSeconds }}s &middot; {{ Math.round(mic.recordedBytes / 1024) }} KB
          </span>
        </div>
        <audio :src="mic.recordedUrl" controls preload="auto" style="width: 100%"></audio>
        <p class="small text-muted mb-0 mt-2">
          Listen back to check the volume, clarity, and background noise.
          If it sounds too quiet, move the microphone closer or raise your
          OS input volume. If you hear hiss or echo, the mic may be picking
          up your speakers — use headphones or a separate mic.
        </p>
        <div class="mt-2">
          <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="clearRecording">
            Discard recording
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Troubleshooting -->
  <div class="card border-0 shadow-sm mb-4">
    <div class="card-body">
      <h2 class="h5 mb-2"><i class="bi bi-life-preserver me-2"></i>Troubleshooting</h2>
      <p class="text-muted small mb-3">
        Step-by-step instructions for allowing microphone access in your
        browser. Auto-matched to what we detected — switch with the picker if
        the detection looks wrong.
      </p>

      <div class="mb-3">
        <label class="form-label small mb-1">Show instructions for:</label>
        <select v-model="remedyOverride" class="form-select form-select-sm" style="max-width: 320px">
          <option value="">Auto-detected ({{ env.browser || "Unknown" }}{{ env.os ? " / " + env.os : "" }})</option>
          <option v-for="k in remedyKeys" :key="k" :value="k">{{ k }}</option>
        </select>
      </div>

      <div class="border rounded p-3" style="background: #fafafa">
        <h3 class="h6 mb-3">{{ remedy.title }}</h3>
        <ol class="mb-3 ps-3">
          <li v-for="(step, i) in remedy.steps" :key="i" class="mb-2">{{ step }}</li>
        </ol>
        <div v-if="remedy.notes && remedy.notes.length">
          <p v-for="(note, i) in remedy.notes" :key="i" class="small text-muted mb-1">
            <i class="bi bi-lightbulb me-1"></i> {{ note }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Diagnostics + Reset -->
  <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4 pb-3">
    <button type="button" class="btn btn-outline-secondary" @click="copyDiagnostics">
      <i class="bi bi-clipboard me-1"></i> Copy diagnostics JSON
    </button>
    <button type="button" class="btn btn-link text-muted small" @click="resetAll">Reset all tests</button>
  </div>
  <p v-if="copyMsg" class="small text-success mb-0">{{ copyMsg }}</p>
</template>

<style scoped>
.mic-meter {
  position: relative;
  height: 22px;
  background: #f1f5f9;
  border-radius: 11px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}
.mic-meter-fill {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #22c55e 0%, #eab308 60%, #ef4444 90%);
  transition: width 0.06s linear;
}
.mic-meter-peak {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #1e293b;
  pointer-events: none;
}
</style>
